import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { IndustryProvider, useIndustry } from './IndustryContext';
import TopBar from './components/TopBar';
import Sidebar from './components/Sidebar';
import MarkdownPage from './components/MarkdownPage';
import ApiReference from './pages/ApiReference';
import Overview from './pages/Overview';
import QuickstartPage from './pages/QuickstartPage';
import Product from './pages/Product';
import Fee from './pages/Fee';
import PricingDimensions from './pages/PricingDimensions';
import Meter from './pages/Meter';
import RateCard from './pages/RateCard';
import PricingModels from './pages/PricingModels';
import RateCardCalculator from './pages/RateCardCalculator';
import Proration from './pages/Proration';
import RateCardTimeBased from './pages/RateCardTimeBased';
import ProductAttribute from './pages/ProductAttribute';
import BillableMetric from './pages/BillableMetric';
import Grant from './pages/Grant';
import Plan from './pages/Plan';
import Customer from './pages/Customer';
import Contract from './pages/Contract';
import ContractFeeComponents from './pages/ContractFeeComponents';
import ContractSettings from './pages/ContractSettings';
import ContractCreate from './pages/ContractCreate';
import MilestoneFee from './pages/MilestoneFee';
import Usage from './pages/Usage';
import BillingReport from './pages/BillingReport';
import RateCardWildcard from './pages/RateCardWildcard';
import AiChat from './components/AiChat';

// Pages that need a custom React component instead of a markdown render.
// All other page IDs fall through to <MarkdownPage>, which fetches /content/{id}.md.
// Add new entries here when a page requires interactivity beyond what markdown supports.
const CUSTOM_PAGES = {
  'api-reference':      ApiReference,
  'overview':           Overview,
  'quickstart':         QuickstartPage,
  'product':            Product,
  'fee':                Fee,
  'pricing-dimensions': PricingDimensions,
  'meter':              Meter,
  'product-attribute':  ProductAttribute,
  'billable-metric':    BillableMetric,
  'rate-card':            RateCard,
  'pricing-models':       PricingModels,
  'proration':            Proration,
  'rate-card-time-based': RateCardTimeBased,
  'rate-card-calculator': RateCardCalculator,
  'rate-card-wildcard':   RateCardWildcard,
  'customer':             Customer,
  'contract':                  Contract,
  'contract-fee-components':   ContractFeeComponents,
  'milestone-fee':             MilestoneFee,
  'contract-settings':         ContractSettings,
  'contract-create':           ContractCreate,
  'usage':                     Usage,
  'billing-report':            BillingReport,
  'grant':                     Grant,
  'plan':                      Plan,
};

function initTheme() {
  const saved = localStorage.getItem('theme');
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return saved ?? (prefersDark ? 'dark' : 'light');
}

// Parse URL into { page, subpage }. First segment is the page ID (matches
// CUSTOM_PAGES keys and sidebar entries). Second segment is an optional
// sub-route used by pages like /api-reference that have their own sub-pages.
function pageFromPath() {
  const parts = window.location.pathname.replace(/^\//, '').split('/').filter(Boolean);
  return { page: parts[0] || 'overview', subpage: parts[1] || null };
}

function AppShell() {
  const [route, setRoute] = useState(pageFromPath);
  const { page: activePage, subpage: activeSubpage } = route;
  const [theme, setTheme] = useState(initTheme);
  const [navOpen, setNavOpen] = useState(false);
  const { industries, industryId, setIndustryId } = useIndustry();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  // Sync back/forward navigation
  useEffect(() => {
    const onPop = () => setRoute(pageFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // On initial load, scroll to hash if present
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const timer = setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
    }, 150);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // `target` can be a single id ('quickstart') or a two-segment path
  // ('api-reference/tax-rates'). Existing callers pass a single id.
  const navigate = useCallback((target, sectionId) => {
    const [page, subpage = null] = String(target).split('/');
    const url = `/${page}${subpage ? `/${subpage}` : ''}${sectionId ? `#${sectionId}` : ''}`;
    window.history.pushState(null, '', url);
    setRoute({ page, subpage });
    setNavOpen(false);
    if (sectionId) {
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  const CustomPage = CUSTOM_PAGES[activePage];
  const hideIndustry = activePage === 'api-reference';

  return (
    <div className="app-shell">
      <TopBar
        onNavigate={navigate}
        industries={hideIndustry ? null : industries}
        industryId={industryId}
        onIndustryChange={setIndustryId}
        theme={theme}
        onToggleTheme={toggleTheme}
        navOpen={navOpen}
        onMenuToggle={() => setNavOpen((o) => !o)}
      />
      <div className="app-body">
        {navOpen && <div className="nav-overlay" onClick={() => setNavOpen(false)} />}
        <Sidebar
          active={activePage}
          onNavigate={navigate}
          isOpen={navOpen}
          industries={hideIndustry ? null : industries}
          industryId={industryId}
          onIndustryChange={setIndustryId}
        />
        <main
          className={`doc-main${activePage === 'api-reference' ? ' doc-main--full-height' : ''}`}
          key={activePage}
        >
          {CustomPage
            ? <CustomPage onNavigate={navigate} subpage={activeSubpage} />
            : <MarkdownPage pageId={activePage} onNavigate={navigate} />
          }
        </main>
      </div>
      <AiChat />
    </div>
  );
}

export default function App() {
  return (
    <IndustryProvider>
      <AppShell />
    </IndustryProvider>
  );
}
